import i18next from 'i18next';
import { Updater } from 'widgets-for-react';
import { UserData } from "../../subscription/subscriptionState";
import { buildValidator, isValidatorPassed, Validator, validatorBuilders, ValidatorResult, validatorResultBuilders } from "../../validator/validator";
import { CountryState, initialCountryState } from "../country/countryState";
import { CompanyTypeSelectorState, initialCompanyTypeSelectorState } from '../companyTypeSelector/companyTypeSelectorState';
import { isVATnumber } from 'utils/vat';

export type Company = Partial<{
  name:string
  address:string
  postCode:string
  city:string
  VAT:string
}>

const emptyCompany : Company = {}

const companyValidator = buildValidator<Company>({
  name:[b => b.notEmpty(), validatorBuilders.string("name", () => i18next.t(`subscriptionValidators:CompanyName`))],
  VAT:[b => b.notEmpty() && b.matches(isVATnumber), validatorBuilders.string("VAT", () => i18next.t(`subscriptionValidators:VAT`))],
  address:[b => b.notEmpty(), validatorBuilders.string("address", () => i18next.t(`subscriptionValidators:CompanyAddress`))],
  postCode:[b => b.notEmpty(), validatorBuilders.string("postCode", () => i18next.t(`subscriptionValidators:CompanyPostcode`))],
  city:[b => b.notEmpty(), validatorBuilders.string("city", () => i18next.t(`subscriptionValidators:CompanyCity`))]
})

export type CompanyState = {
  input:Company
  validator:Validator<Company, Company>,
  validatorResult:ValidatorResult
  country:CountryState
  countryFixed:boolean
  companyType:CompanyTypeSelectorState
  isValid():boolean
}

export const initialCompanyState = (userData?:UserData) : CompanyState => {
  let initialCompany = emptyCompany
  if (userData && userData.Company) {
    initialCompany = {
      ...initialCompany,
      VAT:userData?.Company?.VAT,
      address:userData?.Company?.Address,
      city:userData?.Company?.City,
      name:userData?.Company?.Title,
      postCode:userData?.Company?.Postcode
    }
  }

  const countryId = userData?.Company?.Country?.Id
  return ({
    input:initialCompany,
    validator:companyValidator.setValueToValidate(initialCompany,initialCompany),
    validatorResult:validatorResultBuilders.validating(""),
    country:initialCountryState(countryId),
    countryFixed:!!countryId,
    companyType:initialCompanyTypeSelectorState({Title: userData?.Company?.Type}),
    isValid:function(this:CompanyState) { return isValidatorPassed(this.validatorResult) && this.country.isValid() && this.companyType.isValid() }
  })
}

export const companyStateUpdaters = {
  input:(u:Updater<CompanyState["input"]>) : Updater<CompanyState> =>
    s => {
      const newInput = u(s.input)
      return ({...s, input:newInput, validator:s.validator.setValueToValidate(newInput, newInput)})
    },
  validation:(u:Updater<CompanyState["validator"]>) : Updater<CompanyState> =>
    s => {
      const newValidator = u(s.validator)
      return ({...s, validator:newValidator, validatorResult:newValidator.evaluateResult() })
    },
  country:(u:Updater<CompanyState["country"]>) : Updater<CompanyState> =>
    s => ({...s, country:u(s.country)}),
  companyType:(u:Updater<CompanyState["companyType"]>) : Updater<CompanyState> =>
    s => ({...s, companyType:u(s.companyType)}),
}

export const companyUpdaters = {
  name:(u:Updater<Company["name"]>) : Updater<Company> =>
    s => ({...s, name:u(s.name)}),
  address:(u:Updater<Company["address"]>) : Updater<Company> =>
    s => ({...s, address:u(s.address)}),
  postCode:(u:Updater<Company["postCode"]>) : Updater<Company> =>
    s => ({...s, postCode:u(s.postCode)}),
  city:(u:Updater<Company["city"]>) : Updater<Company> =>
    s => ({...s, city:u(s.city)}),
  VAT:(u:Updater<Company["VAT"]>) : Updater<Company> =>
    s => ({...s, VAT:u(s.VAT)}),
}
